import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NEW_TEMPLATE_TEMP_ID } from '@ax/constants';
import { TemplateStateInterface } from './interfaces';

export const templatesInitialStateValue: TemplateStateInterface = {
  items: [],
  current: null,
  discardConfirmation: {
    show: false,
    postDiscardActions: [],
  },
  editedTemplate: null,
  navigatedOutside: null,
};

export const templatesSlice = createSlice({
  name: 'templates',
  initialState: templatesInitialStateValue,
  reducers: {
    templateSelected(state, action) {
      return {
        ...state,
        current: action.payload.template,
      };
    },
    templateDeselected(state, _) {
      return { ...state };
    },
    discardConfirmationRequested(state, action) {
      return {
        ...state,
        discardConfirmation: {
          show: true,
          postDiscardActions: action.payload.postDiscardActions,
        },
      };
    },
    navigatedOutsideTemplatesRequested(state, action) {
      return {
        ...state,
        navigatedOutside: action.payload.item,
      };
    },
    navigatedOutsideTemplatesSucceeded(state, _) {
      return {
        ...state,
        navigatedOutside: null,
      };
    },
    addNewTemplateRequested(state, action) {
      const newItem = {
        id: NEW_TEMPLATE_TEMP_ID,
        name: 'New Template',
        title: 'New Template',
        providerId: action.payload.providerId,
        voiceTrigger: '',
        noteSection: '',
        age: 'All',
        gender: '',
        content: '',
      };

      return {
        ...state,
        items: [newItem, ...state.items.filter((item) => item.id != NEW_TEMPLATE_TEMP_ID)],
        current: { ...newItem },
      };
    },
    loadTemplatesRequested(state, _) {
      return { ...state };
    },
    loadTemplateListSucceeded(state, action) {
      return {
        ...state,
        items: action.payload.templates,
        current: action.payload.templates[0] || null,
      };
    },
    loadTemplatesFailed(state, _: PayloadAction<{ message: string }>) {
      return { ...state };
    },
    createTemplateRequested(state, _) {
      return { ...state };
    },
    createTemplateSucceeded(state, action) {
      return {
        ...state,
        items: [
          ...state.items.filter((item) => item.id != NEW_TEMPLATE_TEMP_ID),
          action.payload.template,
        ],
        current: action.payload.template,
      };
    },
    createTemplateFailed(state, _: PayloadAction<{ message: string }>) {
      return { ...state };
    },
    updateTemplateRequested(state, _) {
      return { ...state };
    },
    updateTemplateSucceeded(state, action) {
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.id === action.payload.template.id) {
            return action.payload.template;
          }
          return { ...item };
        }),
        current: action.payload.template,
      };
    },
    updateTemplateFailed(state, _: PayloadAction<{ message: string }>) {
      return { ...state };
    },
    deleteTemplateRequested(state, _) {
      return { ...state };
    },
    deleteTemplateSucceeded(state, action) {
      const realItems = state.items.filter((item) => item.id !== action.payload.templateId);
      return {
        ...state,
        items: realItems,
        current: realItems.length === 0 ? null : state.items[0],
      };
    },
    newTemplateDiscarded(state, _) {
      const lastItemList = [...state.items];
      return {
        ...state,
        items: state.items.filter((item) => item.id != NEW_TEMPLATE_TEMP_ID),
        current:
          state.current?.id === NEW_TEMPLATE_TEMP_ID
            ? lastItemList.length >= 2
              ? lastItemList[1]
              : null
            : state.current,
      };
    },
    templateFormEdited(state, action) {
      return {
        ...state,
        editedTemplate: {
          templateId: action.payload.templateId,
          isEditing: true,
        },
      };
    },
    templateFormCleaned(state, _) {
      return {
        ...state,
        editedTemplate: null,
      };
    },
    templatediscardConfirmed(state, _) {
      return {
        ...state,
        discardConfirmation: {
          ...state.discardConfirmation,
          show: false,
        },
      };
    },
    templatediscardCancel(state, _) {
      return {
        ...state,
        discardConfirmation: {
          ...state.discardConfirmation,
          show: false,
        },
      };
    },
    templatePostDiscardActionsExecuted(state, _) {
      return {
        ...state,
        discardConfirmation: {
          ...state.discardConfirmation,
          postDiscardActions: [],
        },
      };
    },
    deleteTemplateFailed(state, _: PayloadAction<{ message: string }>) {
      return { ...state };
    },
  },
});

export const {
  loadTemplateListSucceeded,
  addNewTemplateRequested,
  loadTemplatesRequested,
  templateSelected,
  templateDeselected,
  createTemplateRequested,
  templateFormEdited,
  templateFormCleaned,
  navigatedOutsideTemplatesRequested,
  navigatedOutsideTemplatesSucceeded,
  createTemplateSucceeded,
  createTemplateFailed,
  updateTemplateRequested,
  updateTemplateSucceeded,
  updateTemplateFailed,
  deleteTemplateRequested,
  deleteTemplateSucceeded,
  discardConfirmationRequested,
  newTemplateDiscarded,
  templatediscardConfirmed,
  templatediscardCancel,
  deleteTemplateFailed,
} = templatesSlice.actions;

export default templatesSlice.reducer;
